/**
 * @file    ProgramsManagerHeader.tsx
 * @scope   VIEW
 * @summary Header of the CPR Manage Programs screen: programs label, Global filters entry point,
 *          rollup calculation settings (calculation type / decimal places), the add-program action
 *          and the expand-all toggle.
 * @consumes appAssets icons, OptionCheckbox, DataValidationTip, decimalPlaces
 * @produces `ProgramsManagerHeader` component
 * @sideEffects None — presentational; actions bubble up via callbacks.
 * @notes Reuses the Enterprise FieldsManagerHeader classes/styles; the Global filters block is
 *        gated by the same `useDiyMetricLevelFilters` flag.
 */
import React from 'react';
import Select from 'react-select';
import OptionCheckbox from 'core-ui/optionCheckbox/optionCheckbox';
import DataValidationTip from 'components/DataValidation/DataValidationTip';
import { appAssets } from 'constants/assets';
import { decimalPlaces } from 'constants/chartOption';
import { useStore } from 'store/useGlobalStore';
import {
  ROLLUP_AGGREGATION_TYPE_OPTIONS,
  ROLLUP_CALCULATION_TYPE_OPTIONS,
} from './programsManagerModel';
import '../../../Enterprise/Views/FieldsManager/FieldsManagerHeader.scss';
import './ProgramsManagerHeader.scss';

const FilterIcon = appAssets.icons.FILTERNOTAPPLIED;

interface ProgramsManagerHeaderProps {
  /** Display name of the rollup being edited (shown in the programs label). */
  rollupName: string;
  calculationType?: string;
  aggregationType?: string;
  precision?: number;
  /** Text describing how many Global filters are currently applied. */
  filterCountText: string;
  /** True when at least one program is incomplete. */
  hasInvalidPrograms: boolean;
  /** True when at least one partition-level filter is incomplete. */
  hasInvalidPartitionFilters: boolean;
  areAllExpanded: boolean;
  onCalculationTypeChange: (option: any) => void;
  onAggregationTypeChange: (option: any) => void;
  onPrecisionChange: (option: any) => void;
  onAddProgram: () => void;
  onToggleExpandAll: (isExpanded: boolean) => void;
  onManagePartitionFilters?: () => void;
}

const customSelectStyles = {
  control: (styles: any) => ({
    ...styles,
    maxWidth: '100%',
  }),
};

const ProgramsManagerHeader: React.FC<ProgramsManagerHeaderProps> = ({
  rollupName,
  calculationType,
  aggregationType,
  precision,
  filterCountText,
  hasInvalidPrograms,
  hasInvalidPartitionFilters,
  areAllExpanded,
  onCalculationTypeChange,
  onAggregationTypeChange,
  onPrecisionChange,
  onAddProgram,
  onToggleExpandAll,
  onManagePartitionFilters,
}) => {
  const { useDiyMetricLevelFilters } = useStore((state) => ({
    useDiyMetricLevelFilters: state.configDashboard.useDiyMetricLevelFilters,
  }));

  return (
    <div className="fields-manager-header programs-manager-header">
      <div className="fields-manager-header__top">
        <div className="data-source-info">
          <div className="dataSourceFieldsLabel">
            <span>{rollupName}</span>
            <span className="dataSourceFieldsLabel__validation" aria-hidden={!hasInvalidPrograms}>
              {hasInvalidPrograms && (
                <DataValidationTip
                  message="Invalid programs are present on this rollup."
                  kind="error"
                  placement="bottom"
                />
              )}
            </span>
          </div>
        </div>

        {useDiyMetricLevelFilters && (
          <div className="fields-manager-header__partition-filters-wrapper">
            <button
              type="button"
              className="fields-manager-header__partition-filters"
              onClick={() => onManagePartitionFilters?.()}
              title="Global filters"
            >
              <FilterIcon />
            </button>
            <div className="dataSourceFiltersLabel">
              <span>{filterCountText}</span>
              {hasInvalidPartitionFilters && (
                <span className="dataSourceFiltersLabel__validation">
                  <DataValidationTip
                    message="Invalid Global filters are present. They will be automatically removed if you go back."
                    kind="error"
                    placement="bottom"
                  />
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="programs-manager-header__settings">
        <div className="diy-input-container">
          <div className="diy-material-textfield">
            <Select
              className="diy-select"
              inputId="rollup-calculation-type"
              options={ROLLUP_CALCULATION_TYPE_OPTIONS}
              value={
                ROLLUP_CALCULATION_TYPE_OPTIONS.find((o) => o.value === calculationType) ?? null
              }
              onChange={onCalculationTypeChange}
              styles={customSelectStyles}
              placeholder="Select..."
            />
            <label className="diy-label" htmlFor="rollup-calculation-type">
              Calculation Type
            </label>
          </div>
        </div>
        <div className="diy-input-container">
          <div className="diy-material-textfield">
            <Select
              className="diy-select"
              inputId="rollup-aggregation-type"
              options={ROLLUP_AGGREGATION_TYPE_OPTIONS}
              value={
                ROLLUP_AGGREGATION_TYPE_OPTIONS.find((o) => o.value === aggregationType) ?? null
              }
              onChange={onAggregationTypeChange}
              styles={customSelectStyles}
              placeholder="Select..."
            />
            <label className="diy-label" htmlFor="rollup-aggregation-type">
              Aggregation Type
            </label>
          </div>
        </div>
        <div className="diy-input-container">
          <div className="diy-material-textfield">
            <Select
              className="diy-select"
              inputId="rollup-decimal-places"
              options={decimalPlaces}
              value={decimalPlaces.find((o) => o.value === precision) ?? null}
              onChange={onPrecisionChange}
              styles={customSelectStyles}
              placeholder="Select..."
            />
            <label className="diy-label" htmlFor="rollup-decimal-places">
              Decimal Places
            </label>
          </div>
        </div>
      </div>

      <div className="fields-manager-header__bottom programs-manager-header__bottom">
        <button type="button" className="add-field-button" onClick={onAddProgram}>
          + Add program
        </button>
        <div className="fields-manager-header__expand-toggle">
          <OptionCheckbox
            key={`expand-programs-${areAllExpanded}`}
            option={{
              displayName: 'Expand fields',
              actionName: 'expand-programs',
              isChecked: areAllExpanded,
            }}
            onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) =>
              onToggleExpandAll(e.target.checked)
            }
            size="small"
            labelPosition="right"
          />
        </div>
      </div>
    </div>
  );
};

export default ProgramsManagerHeader;
